import { AmountField } from "@/components/catalog/AmountField";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DEAL_TYPE_OPTIONS,
  PROFIT_RULE_OPTIONS,
  clearedDealDependents,
  profitRulesForDealType,
  showsOwnerDemandPrice,
  type ProductDealType,
  type ProductProfitRuleType,
} from "@/lib/catalog/dealPartners";
import type { ProductDraft } from "@/lib/catalog/types";
import { Handshake } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";

type Props = {
  draft: ProductDraft;
  onChange: (patch: Partial<ProductDraft>) => void;
};

export function DealInformationSection({ draft, onChange }: Props) {
  const rule = draft.profitRuleType ?? null;
  const showOwnerDemand = showsOwnerDemandPrice(rule);
  const allowedRules = useMemo(
    () => profitRulesForDealType(draft.dealType ?? null),
    [draft.dealType],
  );
  const ruleOptions = PROFIT_RULE_OPTIONS.filter((o) => allowedRules.includes(o.value));
  const showProfitRule = allowedRules.length > 0;
  const prevDealTypeRef = useRef(draft.dealType);

  // No empty state: default Deal Type to the first option.
  useEffect(() => {
    if (draft.dealType) return;
    const first = DEAL_TYPE_OPTIONS[0].value;
    prevDealTypeRef.current = first;
    onChange({
      dealType: first,
      ...clearedDealDependents(first),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.dealType]);

  useEffect(() => {
    const prev = prevDealTypeRef.current;
    prevDealTypeRef.current = draft.dealType;
    if (prev === draft.dealType) return;
    onChange(clearedDealDependents(draft.dealType ?? null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.dealType]);

  // Dependent dropdown: default Profit Rule to the first allowed option for the deal type.
  useEffect(() => {
    if (!draft.dealType || allowedRules.length === 0) return;
    if (draft.profitRuleType && allowedRules.includes(draft.profitRuleType)) return;
    const next = allowedRules[0];
    const basePurchase =
      draft.purchasePrice != null && Number.isFinite(Number(draft.purchasePrice))
        ? Number(draft.purchasePrice)
        : null;
    const isOwnerSurplus = draft.dealType === "owner_vehicle" && next === "owner_surplus";
    onChange({
      profitRuleType: next,
      ownerDemandPrice: isOwnerSurplus
        ? (basePurchase ?? draft.ownerDemandPrice ?? null)
        : next === "owner_surplus"
          ? draft.ownerDemandPrice
          : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.dealType, draft.profitRuleType, allowedRules]);

  return (
    <div className="rounded-xl border border-border/50 bg-card/40 p-4 space-y-4">
      <div className="flex items-start gap-2">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Handshake className="h-4 w-4" />
        </div>
        <div>
          <p className="text-sm font-semibold text-foreground">Deal information</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            How this vehicle was acquired and how profit is settled.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {showProfitRule ? (
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-foreground">Profit Rule</Label>
            <Select
              value={rule && allowedRules.includes(rule) ? rule : undefined}
              disabled={!draft.dealType}
              onValueChange={(v) => {
                const next = v as ProductProfitRuleType;
                const basePurchase =
                  draft.purchasePrice != null && Number.isFinite(Number(draft.purchasePrice))
                    ? Number(draft.purchasePrice)
                    : null;
                const isOwnerSurplus = draft.dealType === "owner_vehicle" && next === "owner_surplus";
                onChange({
                  profitRuleType: next,
                  ownerDemandPrice: isOwnerSurplus
                    ? (basePurchase ?? draft.ownerDemandPrice ?? null)
                    : next === "owner_surplus"
                      ? draft.ownerDemandPrice
                      : null,
                });
              }}
            >
              <SelectTrigger className="glass-input h-11 text-sm">
                <SelectValue placeholder={draft.dealType ? "Select profit rule" : "Select deal type first"} />
              </SelectTrigger>
              <SelectContent>
                {ruleOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : draft.dealType === "purchased" ? (
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-foreground">Profit Rule</Label>
            <Input className="glass-input h-11 text-sm bg-muted/30" value="Not applicable" readOnly tabIndex={-1} />
          </div>
        ) : (
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-foreground">Profit Rule</Label>
            <Input
              className="glass-input h-11 text-sm bg-muted/30"
              value=""
              placeholder="Select deal type first"
              readOnly
              tabIndex={-1}
            />
          </div>
        )}

        <AmountField
          id="owner-demand-price"
          label={showOwnerDemand ? "Owner Demand Price *" : "Owner Demand Price"}
          value={showOwnerDemand ? draft.ownerDemandPrice : null}
          onChange={(ownerDemandPrice) => onChange({ ownerDemandPrice })}
          disabled={!showOwnerDemand}
          placeholder={showOwnerDemand ? "Owner asking price" : "—"}
        />
      </div>
    </div>
  );
}
